import React, { useState, useEffect } from 'react';
import {
  Database,
  Table,
  RefreshCw,
  HardDrive,
  CheckCircle2,
  FileCode,
  Download,
  Upload,
  Activity,
  Layers,
  Sparkles,
  Server,
  Calendar,
  AlertCircle,
  AlertTriangle,
  ExternalLink,
  ShieldCheck,
  ShieldAlert,
  Eye,
  EyeOff,
  FileText,
  Users,
  Wrench,
  Boxes,
  Building2,
  Tag,
  MessageSquare,
  Settings,
  Globe,
  Laptop,
  Zap,
  Lock,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import {
  getFullDatabaseData,
  downloadServerDataJson,
  restoreFullDatabaseData,
  getMySqlConfig,
  saveMySqlConfig,
  testMySqlConnection,
} from '../utils/storage';
import { downloadSqlDump } from '../utils/sqlExporter';
import { MySqlConfig, MySqlTestResult } from '../types';

export interface TableStat {
  name: string;
  displayName: string;
  description: string;
  count: number;
  category: 'core' | 'inventory' | 'config' | 'logs';
  icon: string;
  columnsCount: number;
  details?: string;
}

interface DatabaseTableMonitorProps {
  onNavigateTab?: (tab: string) => void;
}

export const DatabaseTableMonitor: React.FC<DatabaseTableMonitorProps> = () => {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [lastRefreshedAt, setLastRefreshedAt] = useState<Date>(new Date());
  const [dbData, setDbData] = useState<any>(null);
  const [serverStats, setServerStats] = useState<{ isOnline: boolean; mode: string }>({
    isOnline: true,
    mode: 'Node.js & SQLite/Storage',
  });
  const [downloadingSql, setDownloadingSql] = useState(false);
  const [restoringBackup, setRestoringBackup] = useState(false);
  const [restoreMessage, setRestoreMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<'all' | 'core' | 'inventory' | 'config' | 'logs'>('all');
  const [searchFilter, setSearchFilter] = useState('');

  // MySQL Live Connection State
  const [mysqlConfig, setMysqlConfig] = useState<MySqlConfig>({
    serverMode: 'hostinger',
    host: 'localhost',
    port: 3306,
    database: '',
    user: '',
    password: '',
    ssl: false,
  });
  const [mysqlTestResult, setMysqlTestResult] = useState<MySqlTestResult | null>(null);
  const [testingConnection, setTestingConnection] = useState(false);
  const [savingConfig, setSavingConfig] = useState(false);
  const [showConfigEdit, setShowConfigEdit] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [configSaveSuccess, setConfigSaveSuccess] = useState(false);

  const loadData = async (showSpin = false) => {
    if (showSpin) setRefreshing(true);
    try {
      const data = await getFullDatabaseData();
      setDbData(data);
      setLastRefreshedAt(new Date());

      // Fetch MySQL config if not currently actively editing
      if (!showConfigEdit) {
        const cfg = await getMySqlConfig();
        setMysqlConfig(cfg);
        // Test MySQL connection only if credentials are provided
        if (cfg.host && cfg.database && cfg.user) {
          await runTestConnection(cfg);
        } else {
          setMysqlTestResult(null);
        }
      }

      // Check server health/info
      try {
        const res = await fetch('/api.php?action=test');
        if (res.ok) {
          setServerStats({ isOnline: true, mode: 'Node.js Full API' });
        }
      } catch {
        // fallback
      }
    } catch (err) {
      console.error('Error fetching database table stats:', err);
    } finally {
      setLoading(false);
      if (showSpin) setRefreshing(false);
    }
  };

  const runTestConnection = async (configToTest?: MySqlConfig) => {
    // Test her zaman PHP sunucusuna gider. Eksik/boş istemci alanları
    // private_config.php içindeki sunucu ayarlarını ezmemelidir.
    const cfg = configToTest || mysqlConfig;
    const safeCfg: MySqlConfig = {
      serverMode: 'hostinger',
      host: cfg.host || 'localhost',
      port: cfg.port || 3306,
      database: cfg.database || '',
      user: cfg.user || '',
      password: cfg.password || '',
      ssl: !!cfg.ssl,
    };
    setMysqlConfig(safeCfg);
    setTestingConnection(true);
    try {
      // Parolayı tarayıcıdan göndermiyoruz. PHP private_config.php'deki parolayı kullanır.
      const res = await testMySqlConnection({
        host: safeCfg.host,
        port: safeCfg.port,
        database: safeCfg.database,
        user: safeCfg.user,
        ssl: safeCfg.ssl,
      });
      setMysqlTestResult(res);
    } catch (err: any) {
      setMysqlTestResult({
        success: false,
        message: 'Bağlantı test edilemedi: ' + err.message,
        error: err.message,
        errorCode: 'CLIENT_ERROR',
        troubleshooting: 'Sunucuyla iletişim kurulurken bir istemci hatası oluştu.',
      });
    } finally {
      setTestingConnection(false);
    }
  };

  const handleClearMysqlConfig = () => {
    setMysqlConfig({
      serverMode: 'hostinger',
      host: '',
      port: 3306,
      database: '',
      user: '',
      password: '',
      ssl: false,
    });
    setMysqlTestResult(null);
  };

  const handleSaveMysqlConfig = async (e?: React.FormEvent | React.SyntheticEvent) => {
    if (e && typeof e.preventDefault === 'function') e.preventDefault();
    setSavingConfig(true);
    setConfigSaveSuccess(false);
    try {
      const res = await saveMySqlConfig(mysqlConfig);
      if (res.success) {
        if (res.config) {
          setMysqlConfig(res.config);
        }
        setConfigSaveSuccess(true);
        setTimeout(() => setConfigSaveSuccess(false), 4000);
        if (res.config?.host && res.config?.database && res.config?.user) {
          await runTestConnection(res.config || mysqlConfig);
        } else {
          setMysqlTestResult(null);
        }
      } else {
        alert('Ayarlar kaydedilemedi: ' + (res.message || 'Bilinmeyen hata'));
      }
    } catch (err: any) {
      alert('Ayarlar kaydedilirken hata: ' + err.message);
    } finally {
      setSavingConfig(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const handleDownloadSql = async () => {
    try {
      setDownloadingSql(true);
      await downloadSqlDump();
    } catch (err: any) {
      console.error('SQL export error:', err);
    } finally {
      setDownloadingSql(false);
    }
  };

  const handleRestoreFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (
      !confirm(
        'Seçilen yedek dosyasını geri yüklemek istediğinize emin misiniz? Bu işlem sunucudaki ve tarayıcıdaki tüm verileri güncelleyecektir.'
      )
    ) {
      e.target.value = '';
      return;
    }

    setRestoringBackup(true);
    setRestoreMessage(null);

    try {
      const text = await file.text();
      const json = JSON.parse(text);
      const res = await restoreFullDatabaseData(json);
      if (res.success) {
        setRestoreMessage({
          type: 'success',
          text: 'Yedek başarıyla geri yüklendi! Sayfa yenileniyor...',
        });
        setTimeout(() => {
          window.location.reload();
        }, 1500);
      } else {
        setRestoreMessage({
          type: 'error',
          text: res.message || 'Yedek yüklenirken bir hata oluştu.',
        });
      }
    } catch (err: any) {
      setRestoreMessage({ type: 'error', text: 'Geçersiz JSON yedek dosyası: ' + err.message });
    } finally {
      setRestoringBackup(false);
      e.target.value = '';
    }
  };

  if (loading) {
    return (
      <div className="p-8 text-center flex flex-col items-center justify-center space-y-3">
        <RefreshCw className="w-8 h-8 text-blue-600 animate-spin" />
        <div className="text-sm font-semibold text-slate-700">Veritabanı tabloları ve MySQL bağlantısı kontrol ediliyor...</div>
      </div>
    );
  }

  // Calculate table rows count
  const ticketsCount = (dbData?.tickets || []).length;
  
  // Calculate relational sub-tables
  let ticketPartsCount = 0;
  let ticketPaymentsCount = 0;
  (dbData?.tickets || []).forEach((t: any) => {
    if (Array.isArray(t.parts)) {
      ticketPartsCount += t.parts.length;
    }
    if (Array.isArray(t.payments)) {
      ticketPaymentsCount += t.payments.length;
    } else if (t.paymentDetails && typeof t.paymentDetails === 'object') {
      const pd = t.paymentDetails;
      if (pd.cash || pd.creditCard || pd.bankTransfer) {
        ticketPaymentsCount += 1;
      }
    }
  });

  const inventoryCount = (dbData?.inventory || []).length;
  let inventoryLotsCount = 0;
  (dbData?.inventory || []).forEach((item: any) => {
    if (Array.isArray(item.lots)) {
      inventoryLotsCount += item.lots.length;
    }
  });

  const stockMovementsCount = (dbData?.stockMovements || []).length;
  const usersCount = (dbData?.users || []).length;
  const contractedCompaniesCount = (dbData?.contractedCompanies || []).length;
  
  let contractedPaymentsCount = 0;
  (dbData?.contractedCompanies || []).forEach((c: any) => {
    if (Array.isArray(c.payments)) {
      contractedPaymentsCount += c.payments.length;
    }
  });

  const deviceTypesCount = (dbData?.deviceTypes || []).length;
  const deviceBrandsCount = (dbData?.deviceBrands || []).length;
  const inventoryCategoriesCount = (dbData?.inventoryCategories || []).length;
  const smsLogsCount = (dbData?.smsLogs || []).length;
  const companyCount = dbData?.company ? 1 : 0;
  const exchangeRateCount = dbData?.exchangeRate ? 1 : 0;
  const labelConfigCount = dbData?.labelConfig ? 1 : 0;
  const smsSettingsCount = dbData?.smsSettings ? 1 : 0;

  const tables: TableStat[] = [
    {
      name: 'tickets',
      displayName: 'Servis Kayıtları & Fişler',
      description: 'Tüm aktif ve tamamlanan teknik servis onarım kayıtları, müşteri ve cihaz detayları',
      count: ticketsCount,
      category: 'core',
      icon: 'FileText',
      columnsCount: 38,
      details: `${(dbData?.tickets || []).filter((t: any) => t.status === 'TeslimEdildi').length} teslim, ${(dbData?.tickets || []).filter((t: any) => t.status !== 'TeslimEdildi' && t.status !== 'Iptal' && t.status !== 'IadeEdildi').length} aktif servis`,
    },
    {
      name: 'ticket_parts',
      displayName: 'Servis Parça Kullanım Hareketleri',
      description: 'Servis kayıtlarına eklenen yedek parçalar, birim alış/satış fiyatları ve sarf miktarları',
      count: ticketPartsCount,
      category: 'core',
      icon: 'Wrench',
      columnsCount: 14,
      details: `Servis fişlerine bağlı detaylı parça hareketleri`,
    },
    {
      name: 'ticket_payments',
      displayName: 'Servis Tahsilat & Ödemeleri',
      description: 'Fiş bazlı nakit, kredi kartı ve havale/EFT ödeme ve tahsilat kayıtları',
      count: ticketPaymentsCount,
      category: 'core',
      icon: 'Activity',
      columnsCount: 10,
      details: 'Kasa ve servis tahsilat geçmişi',
    },
    {
      name: 'inventory',
      displayName: 'Stok & Yedek Parça Envanteri',
      description: 'Yedek parçalar, sarf malzemeleri, adetler, TL/Dolar maliyetleri ve raf konumları',
      count: inventoryCount,
      category: 'inventory',
      icon: 'Boxes',
      columnsCount: 22,
      details: `${(dbData?.inventory || []).reduce((acc: number, i: any) => acc + (Number(i.quantity) || 0), 0)} toplam fiziksel parça`,
    },
    {
      name: 'inventory_lots',
      displayName: 'Stok Alım Partileri (Lots)',
      description: 'Farklı tarihlerde farklı kurlarla alınan parti maliyetleri ve parti bazlı kalan stoklar',
      count: inventoryLotsCount,
      category: 'inventory',
      icon: 'Layers',
      columnsCount: 10,
      details: 'En düşük maliyetli parti (FIFO) eşleştirme tablosu',
    },
    {
      name: 'stock_movements',
      displayName: 'Stok Giriş & Çıkış Hareketleri',
      description: 'Tüm stok alımları, servis tüketimleri, iadeler ve manuel miktar düzeltme logları',
      count: stockMovementsCount,
      category: 'inventory',
      icon: 'Activity',
      columnsCount: 16,
      details: 'Giriş, çıkış, sarf ve iade denetim günlüğü',
    },
    {
      name: 'users',
      displayName: 'Kullanıcılar & Personel Hesapları',
      description: 'Sistem yöneticileri, karşılama personeli ve teknisyenlerin yetki ve giriş kayıtları',
      count: usersCount,
      category: 'config',
      icon: 'Users',
      columnsCount: 11,
      details: `${(dbData?.users || []).filter((u: any) => u.isActive !== false).length} aktif personel hesabı`,
    },
    {
      name: 'contracted_companies',
      displayName: 'Sözleşmeli Cari Firmalar',
      description: 'Kurumsal bakım anlaşmalı şirketler, bakiye, sözleşme bedeli ve iletişim bilgileri',
      count: contractedCompaniesCount,
      category: 'core',
      icon: 'Building2',
      columnsCount: 15,
      details: 'Anlaşmalı kurumsal müşteri kartları',
    },
    {
      name: 'contracted_company_payments',
      displayName: 'Sözleşmeli Firma Tahsilatları',
      description: 'Sözleşmeli firmalara ait tahsilat, bakiye düşümü ve makbuz kayıtları',
      count: contractedPaymentsCount,
      category: 'core',
      icon: 'FileText',
      columnsCount: 9,
      details: 'Cari sözleşme tahsilat geçmişi',
    },
    {
      name: 'device_types',
      displayName: 'Cihaz Türleri Listesi',
      description: 'Telefon, Bilgisayar, Tablet, Akıllı Saat vb. kayıt kategorileri',
      count: deviceTypesCount,
      category: 'config',
      icon: 'Tag',
      columnsCount: 3,
      details: 'Servis kabul açılır menü tanımları',
    },
    {
      name: 'device_brands',
      displayName: 'Kayıtlı Cihaz Markaları',
      description: 'Apple, Samsung, Xiaomi, Asus vb. cihaz markaları ve otomatik tamamlama listesi',
      count: deviceBrandsCount,
      category: 'config',
      icon: 'Tag',
      columnsCount: 3,
      details: 'Otomatik tamamlama marka veritabanı',
    },
    {
      name: 'inventory_categories',
      displayName: 'Stok & Parça Kategorileri',
      description: 'Ekran, Batarya, Kasa, Şarj Soketi, Entegre vb. parça grupları',
      count: inventoryCategoriesCount,
      category: 'config',
      icon: 'Tag',
      columnsCount: 3,
      details: 'Envanter filtreleme grupları',
    },
    {
      name: 'company_info',
      displayName: 'Firma & Fatura Ayarları',
      description: 'İşletme adı, adres, telefon, vergi dairesi/no, logo ve servis koşulları',
      count: companyCount,
      category: 'config',
      icon: 'Building2',
      columnsCount: 14,
      details: dbData?.company?.name || 'Firma profili aktif',
    },
    {
      name: 'sms_logs',
      displayName: 'SMS Gönderim Günlüğü',
      description: 'Müşterilere otomatik veya manuel gönderilen SMS mesajları, durum ve tarihleri',
      count: smsLogsCount,
      category: 'logs',
      icon: 'MessageSquare',
      columnsCount: 7,
      details: 'İletimerkezi SMS iletim kayıtları',
    },
    {
      name: 'sms_settings',
      displayName: 'SMS Entegrasyon & Şablonları',
      description: 'İletimerkezi API anahtarları, başlık (Originator) ve durum SMS şablon metinleri',
      count: smsSettingsCount,
      category: 'config',
      icon: 'MessageSquare',
      columnsCount: 8,
      details: dbData?.smsSettings?.enabled ? 'SMS entegrasyonu aktif' : 'SMS pasif',
    },
    {
      name: 'exchange_rate_history',
      displayName: 'Döviz Kuru Bilgisi (USD/TL)',
      description: 'TCMB canlı kuru veya manuel döviz kuru tanımları ve son güncelleme zamanı',
      count: exchangeRateCount,
      category: 'config',
      icon: 'Activity',
      columnsCount: 6,
      details: `1 USD = ${dbData?.exchangeRate?.rate || 47.96} ₺`,
    },
    {
      name: 'label_config',
      displayName: 'Termal Barkod & Etiket Şablonu',
      description: '40x20mm, 50x30mm termal etiket genişlik, yükseklik ve QR/Barkod baskı ayarları',
      count: labelConfigCount,
      category: 'config',
      icon: 'Tag',
      columnsCount: 12,
      details: 'Termal yazıcı parametreleri',
    },
  ];

  const totalTables = tables.length;
  const totalRecords = tables.reduce((sum, t) => sum + t.count, 0);

  const filteredTables = tables.filter((t) => {
    const matchesCategory = selectedCategory === 'all' || t.category === selectedCategory;
    const matchesSearch =
      t.name.toLowerCase().includes(searchFilter.toLowerCase()) ||
      t.displayName.toLowerCase().includes(searchFilter.toLowerCase()) ||
      t.description.toLowerCase().includes(searchFilter.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  return (
    <div className="space-y-6">
      {/* Top Banner & Quick Stats */}
      <div className="bg-gradient-to-r from-slate-900 via-indigo-950 to-blue-950 rounded-2xl p-5 text-white border border-slate-800 shadow-xl relative overflow-hidden">
        <div className="absolute right-0 top-0 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl pointer-events-none -mr-20 -mt-20"></div>
        
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 relative z-10">
          <div className="space-y-1.5">
            <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-blue-500/20 text-blue-300 border border-blue-400/30 text-xs font-semibold">
              <Database className="w-3.5 h-3.5 text-blue-400" />
              <span>Canlı Veritabanı & Tablo Takip Merkezi</span>
            </div>
            <h3 className="text-xl font-extrabold tracking-tight text-white flex items-center gap-2">
              <span>Veritabanı Durumu & Tablo İstatistikleri</span>
            </h3>
            <p className="text-xs text-slate-300 max-w-2xl leading-relaxed">
              Sistemdeki tüm SQL tabloları, ilişkisel alt tablolar ve her bir tabloda bulunan anlık toplam kayıt adetlerini bu panelden takip edebilirsiniz.
            </p>
          </div>

          <div className="flex items-center gap-2.5 shrink-0">
            <button
              type="button"
              onClick={() => loadData(true)}
              disabled={refreshing || testingConnection}
              className="px-3.5 py-2 bg-white/10 hover:bg-white/20 text-white rounded-xl text-xs font-bold flex items-center gap-2 border border-white/15 transition-all cursor-pointer shadow-xs"
              title="Kayıt sayılarını ve bağlantıyı anlık yeniden tara"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${refreshing || testingConnection ? 'animate-spin' : ''}`} />
              <span>{refreshing || testingConnection ? 'Yenileniyor...' : 'Verileri Yenile'}</span>
            </button>

            <button
              type="button"
              onClick={handleDownloadSql}
              disabled={downloadingSql}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-500 disabled:bg-blue-400 text-white rounded-xl text-xs font-bold flex items-center gap-2 shadow-md shadow-blue-600/30 transition-all cursor-pointer"
            >
              <Download className={`w-3.5 h-3.5 ${downloadingSql ? 'animate-bounce' : ''}`} />
              <span>{downloadingSql ? 'Oluşturuluyor...' : 'MySQL SQL Yedeği Al'}</span>
            </button>
          </div>
        </div>

        {/* 4-Box Summary Dashboard */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-5 pt-4 border-t border-white/10">
          <div className="bg-white/5 border border-white/10 rounded-xl p-3 backdrop-blur-xs">
            <div className="text-[11px] text-slate-400 font-semibold uppercase tracking-wider flex items-center gap-1.5">
              <Table className="w-3.5 h-3.5 text-blue-400" />
              <span>Toplam Tablo Sayısı</span>
            </div>
            <div className="text-2xl font-black text-white mt-1 font-mono">{totalTables}</div>
            <div className="text-[10px] text-blue-300 mt-0.5">17 Ayrı SQL Tablosu</div>
          </div>

          <div className="bg-white/5 border border-white/10 rounded-xl p-3 backdrop-blur-xs">
            <div className="text-[11px] text-slate-400 font-semibold uppercase tracking-wider flex items-center gap-1.5">
              <Layers className="w-3.5 h-3.5 text-emerald-400" />
              <span>Toplam Kayıt Sayısı</span>
            </div>
            <div className="text-2xl font-black text-emerald-400 mt-1 font-mono">{totalRecords}</div>
            <div className="text-[10px] text-emerald-300 mt-0.5">Tüm tablolar toplamı</div>
          </div>

          <div className="bg-white/5 border border-white/10 rounded-xl p-3 backdrop-blur-xs">
            <div className="text-[11px] text-slate-400 font-semibold uppercase tracking-wider flex items-center gap-1.5">
              <FileText className="w-3.5 h-3.5 text-amber-400" />
              <span>Servis & Cari Fişleri</span>
            </div>
            <div className="text-2xl font-black text-amber-400 mt-1 font-mono">{ticketsCount + contractedCompaniesCount}</div>
            <div className="text-[10px] text-amber-300 mt-0.5">{ticketsCount} servis + {contractedCompaniesCount} cari</div>
          </div>

          <div className="bg-white/5 border border-white/10 rounded-xl p-3 backdrop-blur-xs">
            <div className="text-[11px] text-slate-400 font-semibold uppercase tracking-wider flex items-center gap-1.5">
              <Boxes className="w-3.5 h-3.5 text-purple-400" />
              <span>Stok & Hareketler</span>
            </div>
            <div className="text-2xl font-black text-purple-400 mt-1 font-mono">{inventoryCount + stockMovementsCount}</div>
            <div className="text-[10px] text-purple-300 mt-0.5">{inventoryCount} ürün + {stockMovementsCount} hareket</div>
          </div>
        </div>
      </div>

      {/* ========================================================================= */}
      {/* MYSQL CANLI VERİTABANI BAĞLANTI DURUMU & HATA KONTROL MERKEZİ */}
      {/* ========================================================================= */}
      <div className="bg-white rounded-2xl border border-slate-200/90 shadow-sm overflow-hidden">
        {/* Header Bar */}
        <div className="px-5 py-3.5 bg-slate-50 border-b border-slate-200 flex flex-col sm:flex-row sm:items-center justify-between gap-2.5">
          <div className="flex items-center space-x-2.5">
            <div className="p-1.5 rounded-lg bg-blue-100 text-blue-700">
              <Database className="w-4 h-4" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h4 className="text-xs font-bold text-slate-900">MySQL Canlı Veritabanı Bağlantısı</h4>
                {(!mysqlConfig.host || !mysqlConfig.database || !mysqlConfig.user) ? (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-amber-100 text-amber-800 border border-amber-300">
                    <AlertCircle className="w-3 h-3 text-amber-600" />
                    <span>Yapılandırılmadı (Boş)</span>
                  </span>
                ) : mysqlTestResult ? (
                  mysqlTestResult.success ? (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-100 text-emerald-800 border border-emerald-300">
                      <CheckCircle2 className="w-3 h-3 text-emerald-600" />
                      <span>Bağlantı Aktif &amp; Çevrimiçi</span>
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-rose-100 text-rose-800 border border-rose-300 animate-pulse">
                      <AlertCircle className="w-3 h-3 text-rose-600" />
                      <span>Bağlantı Hatası Algılandı</span>
                    </span>
                  )
                ) : (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-slate-100 text-slate-700">
                    <RefreshCw className="w-3 h-3 animate-spin text-slate-500" />
                    <span>Kontrol Ediliyor...</span>
                  </span>
                )}
              </div>
              <p className="text-[11px] text-slate-500 font-mono">
                {mysqlConfig.host || mysqlConfig.database || mysqlConfig.user ? (
                  <>
                    Host: <strong className="text-slate-800">{mysqlConfig.host || '-'}</strong> | Veritabanı: <strong className="text-slate-800">{mysqlConfig.database || '-'}</strong> | Kullanıcı: <strong className="text-slate-800">{mysqlConfig.user || '-'}</strong>
                  </>
                ) : (
                  <span className="text-slate-400 italic font-sans">MySQL bağlantı bilgileri boş. Tanımlamak için 'Bağlantı Bilgilerini Düzenle' butonuna tıklayınız.</span>
                )}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => runTestConnection(mysqlConfig)}
              disabled={testingConnection}
              className="px-3 py-1.5 bg-blue-50 hover:bg-blue-100 text-blue-700 border border-blue-200 rounded-lg text-xs font-bold flex items-center gap-1.5 transition-colors cursor-pointer"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${testingConnection ? 'animate-spin' : ''}`} />
              <span>{testingConnection ? 'Test Ediliyor...' : 'Bağlantıyı Test Et'}</span>
            </button>

            <button
              type="button"
              onClick={() => setShowConfigEdit(!showConfigEdit)}
              className="px-3 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 border border-slate-300 rounded-lg text-xs font-bold flex items-center gap-1.5 transition-colors cursor-pointer"
            >
              <Settings className="w-3.5 h-3.5" />
              <span>{showConfigEdit ? 'Bilgileri Gizle' : 'Bağlantı Bilgilerini Düzenle'}</span>
              {showConfigEdit ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
            </button>
          </div>
        </div>

        {/* Live Status Content & Diagnostic Alerts */}
        <div className="p-4 space-y-3">
          {/* SUCCESS BANNER */}
          {mysqlTestResult && mysqlTestResult.success && (
            <div className="p-3.5 bg-emerald-50/90 border border-emerald-300 rounded-xl text-xs text-emerald-900 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div className="flex items-start space-x-2.5">
                <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0 mt-0.5" />
                <div>
                  <div className="font-bold text-emerald-950">MySQL Veritabanı Bağlantısı Başarılı ve Sorunsuz!</div>
                  <div className="text-[11px] text-emerald-800 mt-0.5">
                    Sunucu Sürümü: <strong>{mysqlTestResult.serverVersion || 'MySQL 8.0+'}</strong> | Gecikme: <strong>{mysqlTestResult.pingMs} ms</strong> | Mevcut Tablolar: <strong>{mysqlTestResult.tablesCount} adet</strong>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <button
                  type="button"
                  onClick={handleDownloadSql}
                  className="px-3 py-1 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-xs font-bold flex items-center gap-1.5 shadow-xs transition-colors cursor-pointer"
                >
                  <Download className="w-3 h-3" />
                  <span>SQL Yedek Al</span>
                </button>
              </div>
            </div>
          )}

          {/* CRITICAL ERROR & TROUBLESHOOTING BANNER (Requested Feature) */}
          {mysqlTestResult && !mysqlTestResult.success && (
            <div className="p-4 bg-rose-50 border-2 border-rose-400/80 rounded-xl text-xs text-rose-950 space-y-3 animate-in fade-in">
              <div className="flex items-start space-x-3">
                <div className="p-2 rounded-lg bg-rose-100 text-rose-700 shrink-0">
                  <ShieldAlert className="w-5 h-5" />
                </div>
                <div className="space-y-1 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-extrabold text-sm text-rose-950">MySQL Veritabanına Bağlanılamadı!</span>
                    {mysqlTestResult.errorCode && (
                      <span className="px-2 py-0.5 rounded-md bg-rose-200 text-rose-900 font-mono font-bold text-[10px]">
                        HATA KODU: {mysqlTestResult.errorCode}
                      </span>
                    )}
                  </div>
                  <div className="text-xs font-semibold text-rose-900 leading-relaxed">
                    {mysqlTestResult.message}
                  </div>
                </div>
              </div>

              {/* Troubleshooting Guide Box */}
              {mysqlTestResult.troubleshooting && (
                <div className="bg-white/80 p-3 rounded-lg border border-rose-200/90 text-[11px] space-y-1.5">
                  <div className="font-bold text-rose-900 flex items-center gap-1.5">
                    <AlertTriangle className="w-3.5 h-3.5 text-amber-600" />
                    <span>Çözüm Adımları &amp; Teşhis Önerisi:</span>
                  </div>
                  <p className="text-slate-700 leading-relaxed">
                    {mysqlTestResult.troubleshooting}
                  </p>
                  <div className="pt-1.5 border-t border-rose-100 flex flex-wrap gap-2 text-[10px] text-slate-600">
                    <span>💡 <strong>Hostinger / cPanel:</strong> hPanel &gt; Veritabanları &gt; <em>Uzak MySQL (Remote MySQL)</em> alanından IP adresinize veya <code>%</code> (herhangi bir ana bilgisayar) erişim izni verildiğinden emin olun.</span>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Config Quick Details or Edit Form */}
          {showConfigEdit ? (
            <div
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleSaveMysqlConfig();
                }
              }}
              className="bg-slate-50 p-4 rounded-xl border border-slate-200 space-y-4"
            >
              <div className="flex flex-col sm:flex-row sm:items-center justify-between border-b border-slate-200 pb-2 gap-2">
                <div className="font-bold text-xs text-slate-800 flex items-center gap-1.5">
                  <Lock className="w-3.5 h-3.5 text-blue-600" />
                  <span>MySQL Bağlantı &amp; Sunucu Modu Seçimi</span>
                </div>
                
                {/* Hızlı Sunucu Modu Butonları (Hostinger vs Yerel) */}
                <div className="inline-flex rounded-lg p-0.5 bg-slate-200/80 text-[11px] font-bold">
                  <button
                    type="button"
                    onClick={() => setMysqlConfig({
                      ...mysqlConfig,
                      serverMode: 'hostinger',
                    })}
                    className={`px-2.5 py-1 rounded-md transition-all cursor-pointer flex items-center gap-1 ${
                      mysqlConfig.serverMode === 'hostinger' || (!mysqlConfig.serverMode && mysqlConfig.host !== 'localhost')
                        ? 'bg-purple-600 text-white shadow-2xs'
                        : 'text-slate-700 hover:text-slate-900'
                    }`}
                  >
                    <Globe className="w-3 h-3" />
                    <span>Hostinger / Uzak Sunucu</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setMysqlConfig({
                      ...mysqlConfig,
                      serverMode: 'local',
                      host: mysqlConfig.host || 'localhost',
                    })}
                    className={`px-2.5 py-1 rounded-md transition-all cursor-pointer flex items-center gap-1 ${
                      mysqlConfig.serverMode === 'local' || mysqlConfig.host === 'localhost' || mysqlConfig.host === '127.0.0.1'
                        ? 'bg-blue-600 text-white shadow-2xs'
                        : 'text-slate-700 hover:text-slate-900'
                    }`}
                  >
                    <Laptop className="w-3 h-3" />
                    <span>Yerel (Local) XAMPP / PC</span>
                  </button>
                </div>
              </div>

              {/* Server Mode Explanation / Guide Banner */}
              {mysqlConfig.serverMode === 'hostinger' ? (
                <div className="bg-purple-50/80 border border-purple-200 rounded-lg p-2.5 text-[11px] text-purple-900 flex items-start gap-2">
                  <Globe className="w-4 h-4 text-purple-600 shrink-0 mt-0.5" />
                  <div>
                    <strong className="text-purple-950">Hostinger Uzak MySQL Kurulum Bilgisi:</strong> Hostinger hPanel &gt; <em>Veritabanları &gt; Uzak MySQL (Remote MySQL)</em> menüsünden <strong>%</strong> (herhangi bir ana bilgisayar) veya sunucu IP adresine izin veriniz. Hostinger MySQL sunucu IP / hostname adresini aşağıdaki "Sunucu / Host Adresi" alanına yazınız.
                  </div>
                </div>
              ) : (
                <div className="bg-blue-50/80 border border-blue-200 rounded-lg p-2.5 text-[11px] text-blue-900 flex items-start gap-2">
                  <Laptop className="w-4 h-4 text-blue-600 shrink-0 mt-0.5" />
                  <div>
                    <strong className="text-blue-950">Yerel Sunucu (Localhost) Kurulum Bilgisi:</strong> Bilgisayarınızdaki XAMPP, WampServer veya MySQL servisi açık olduğunda <code>localhost:3306</code> üzerinden bağlanabilirsiniz.
                  </div>
                </div>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                {/* Host */}
                <div>
                  <label className="block text-[11px] font-bold text-slate-700 mb-1">
                    Sunucu / Host Adresi (IP veya Domain)
                  </label>
                  <input
                    type="text"
                    value={mysqlConfig.host}
                    onChange={(e) => setMysqlConfig({ ...mysqlConfig, host: e.target.value })}
                    placeholder="Örn: srvXXX.main-hosting.eu veya localhost"
                    className="w-full px-3 py-1.5 text-xs bg-white border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 font-mono font-semibold text-slate-800"
                  />
                  <span className="text-[10px] text-slate-400 mt-0.5 block">Hostinger için: sunucu IP / host adı</span>
                </div>

                {/* Port */}
                <div>
                  <label className="block text-[11px] font-bold text-slate-700 mb-1">
                    Port
                  </label>
                  <input
                    type="number"
                    value={mysqlConfig.port || ''}
                    onChange={(e) => setMysqlConfig({ ...mysqlConfig, port: Number(e.target.value) || 3306 })}
                    placeholder="3306"
                    className="w-full px-3 py-1.5 text-xs bg-white border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 font-mono"
                  />
                  <span className="text-[10px] text-slate-400 mt-0.5 block">Standart: 3306</span>
                </div>

                {/* Database */}
                <div>
                  <label className="block text-[11px] font-bold text-slate-700 mb-1">
                    Veritabanı Adı (Database)
                  </label>
                  <input
                    type="text"
                    value={mysqlConfig.database}
                    onChange={(e) => setMysqlConfig({ ...mysqlConfig, database: e.target.value })}
                    placeholder="Örn: u123456_veritabani"
                    className="w-full px-3 py-1.5 text-xs bg-white border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 font-mono font-bold text-blue-700"
                  />
                  <span className="text-[10px] text-slate-400 mt-0.5 block">MySQL veritabanınızın tam adı</span>
                </div>

                {/* User */}
                <div>
                  <label className="block text-[11px] font-bold text-slate-700 mb-1">
                    Veritabanı Kullanıcısı (User)
                  </label>
                  <input
                    type="text"
                    value={mysqlConfig.user}
                    onChange={(e) => setMysqlConfig({ ...mysqlConfig, user: e.target.value })}
                    placeholder="Örn: u123456_kullanici"
                    className="w-full px-3 py-1.5 text-xs bg-white border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 font-mono"
                  />
                  <span className="text-[10px] text-slate-400 mt-0.5 block">MySQL yetkili kullanıcı adı</span>
                </div>

                {/* Password */}
                <div>
                  <label className="block text-[11px] font-bold text-slate-700 mb-1">
                    Kullanıcı Şifresi (Password)
                  </label>
                  <div className="relative">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={mysqlConfig.password || ''}
                      onChange={(e) => setMysqlConfig({ ...mysqlConfig, password: e.target.value })}
                      placeholder="Veritabanı şifresi"
                      className="w-full pl-3 pr-8 py-1.5 text-xs bg-white border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 font-mono"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 cursor-pointer"
                    >
                      {showPassword ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                    </button>
                  </div>
                  <span className="text-[10px] text-slate-400 mt-0.5 block">Kullanıcıya atanmış parola</span>
                </div>

                {/* SSL Option */}
                <div className="flex items-center pt-5">
                  <label className="inline-flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={!!mysqlConfig.ssl}
                      onChange={(e) => setMysqlConfig({ ...mysqlConfig, ssl: e.target.checked })}
                      className="rounded border-slate-300 text-blue-600 focus:ring-blue-500 w-4 h-4"
                    />
                    <span className="text-xs font-semibold text-slate-700">Güvenli SSL Bağlantısı (TLS)</span>
                  </label>
                </div>
              </div>

              {configSaveSuccess && (
                <div className="p-2.5 bg-emerald-50 border border-emerald-300 rounded-lg text-xs text-emerald-800 font-semibold flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                  <span>MySQL bağlantı parametreleri başarıyla kaydedildi ve güncellendi!</span>
                </div>
              )}

              <div className="flex flex-col sm:flex-row items-center justify-between gap-2 pt-2 border-t border-slate-200">
                <button
                  type="button"
                  onClick={handleClearMysqlConfig}
                  className="px-3 py-1.5 text-xs font-semibold text-rose-600 hover:text-rose-700 hover:bg-rose-50 border border-rose-200 rounded-lg cursor-pointer transition-colors"
                >
                  Bilgileri Temizle (Boşalt)
                </button>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setShowConfigEdit(false)}
                    className="px-3 py-1.5 bg-slate-200 hover:bg-slate-300 text-slate-700 rounded-lg text-xs font-bold cursor-pointer"
                  >
                    Kapat
                  </button>
                  <button
                    type="button"
                    onClick={() => handleSaveMysqlConfig()}
                    disabled={savingConfig}
                    className="px-4 py-1.5 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white rounded-lg text-xs font-bold flex items-center gap-1.5 cursor-pointer shadow-xs"
                  >
                    <CheckCircle2 className="w-3.5 h-3.5" />
                    <span>{savingConfig ? 'Kaydediliyor...' : 'Kaydet ve Güncelle'}</span>
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <div className="space-y-2 pt-1">
              <div className="grid grid-cols-2 sm:grid-cols-5 gap-2.5">
                <div className="bg-slate-50 p-2.5 rounded-lg border border-slate-200">
                  <div className="text-[10px] uppercase font-bold text-slate-400">Sunucu Modu</div>
                  <div className="text-xs font-bold text-slate-800 mt-0.5 flex items-center gap-1 truncate">
                    {mysqlConfig.serverMode === 'local' || mysqlConfig.host === 'localhost' || mysqlConfig.host === '127.0.0.1' ? (
                      <>
                        <Laptop className="w-3 h-3 text-blue-600" />
                        <span>Yerel (Local)</span>
                      </>
                    ) : (
                      <>
                        <Globe className="w-3 h-3 text-purple-600" />
                        <span>Hostinger Uzak</span>
                      </>
                    )}
                  </div>
                </div>
                <div className="bg-slate-50 p-2.5 rounded-lg border border-slate-200">
                  <div className="text-[10px] uppercase font-bold text-slate-400">Sunucu (Host)</div>
                  <div className="text-xs font-mono font-bold text-slate-800 mt-0.5 truncate">
                    {mysqlConfig.host ? `${mysqlConfig.host}:${mysqlConfig.port || 3306}` : '-'}
                  </div>
                </div>
                <div className="bg-slate-50 p-2.5 rounded-lg border border-slate-200">
                  <div className="text-[10px] uppercase font-bold text-slate-400">Veritabanı (DB)</div>
                  <div className="text-xs font-mono font-bold text-blue-700 mt-0.5 truncate">
                    {mysqlConfig.database || '-'}
                  </div>
                </div>
                <div className="bg-slate-50 p-2.5 rounded-lg border border-slate-200">
                  <div className="text-[10px] uppercase font-bold text-slate-400">Kullanıcı (User)</div>
                  <div className="text-xs font-mono font-bold text-slate-800 mt-0.5 truncate">
                    {mysqlConfig.user || '-'}
                  </div>
                </div>
                <div className="bg-slate-50 p-2.5 rounded-lg border border-slate-200">
                  <div className="text-[10px] uppercase font-bold text-slate-400">Şifre</div>
                  <div className="text-xs font-mono font-bold text-slate-800 mt-0.5">
                    {mysqlConfig.password ? '••••••••' : '(Boş)'}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Restore Message Banner */}
      {restoreMessage && (
        <div
          className={`p-3.5 rounded-xl text-xs font-semibold flex items-center space-x-2 animate-in fade-in ${
            restoreMessage.type === 'success'
              ? 'bg-emerald-50 border border-emerald-300 text-emerald-800'
              : 'bg-rose-50 border border-rose-300 text-rose-800'
          }`}
        >
          {restoreMessage.type === 'success' ? (
            <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
          ) : (
            <AlertCircle className="w-4 h-4 text-rose-600 shrink-0" />
          )}
          <span>{restoreMessage.text}</span>
        </div>
      )}

      {/* Filter & Search Bar */}
      <div className="bg-white p-3.5 rounded-xl border border-slate-200/90 shadow-2xs flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3">
        {/* Category Filter Chips */}
        <div className="flex items-center gap-1.5 overflow-x-auto pb-1 sm:pb-0">
          <button
            type="button"
            onClick={() => setSelectedCategory('all')}
            className={`px-3 py-1.5 text-xs font-bold rounded-lg transition-all cursor-pointer whitespace-nowrap ${
              selectedCategory === 'all'
                ? 'bg-blue-600 text-white shadow-xs'
                : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
            }`}
          >
            Tüm Tablolar ({tables.length})
          </button>
          <button
            type="button"
            onClick={() => setSelectedCategory('core')}
            className={`px-3 py-1.5 text-xs font-bold rounded-lg transition-all cursor-pointer whitespace-nowrap ${
              selectedCategory === 'core'
                ? 'bg-blue-600 text-white shadow-xs'
                : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
            }`}
          >
            Servis &amp; Cari ({tables.filter((t) => t.category === 'core').length})
          </button>
          <button
            type="button"
            onClick={() => setSelectedCategory('inventory')}
            className={`px-3 py-1.5 text-xs font-bold rounded-lg transition-all cursor-pointer whitespace-nowrap ${
              selectedCategory === 'inventory'
                ? 'bg-blue-600 text-white shadow-xs'
                : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
            }`}
          >
            Stok &amp; Envanter ({tables.filter((t) => t.category === 'inventory').length})
          </button>
          <button
            type="button"
            onClick={() => setSelectedCategory('config')}
            className={`px-3 py-1.5 text-xs font-bold rounded-lg transition-all cursor-pointer whitespace-nowrap ${
              selectedCategory === 'config'
                ? 'bg-blue-600 text-white shadow-xs'
                : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
            }`}
          >
            Sistem &amp; Tanımlar ({tables.filter((t) => t.category === 'config').length})
          </button>
          <button
            type="button"
            onClick={() => setSelectedCategory('logs')}
            className={`px-3 py-1.5 text-xs font-bold rounded-lg transition-all cursor-pointer whitespace-nowrap ${
              selectedCategory === 'logs'
                ? 'bg-blue-600 text-white shadow-xs'
                : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
            }`}
          >
            Log &amp; SMS ({tables.filter((t) => t.category === 'logs').length})
          </button>
        </div>

        {/* Search input */}
        <div className="relative w-full sm:w-64">
          <input
            type="text"
            placeholder="Tablo adı veya açıklamasında ara..."
            value={searchFilter}
            onChange={(e) => setSearchFilter(e.target.value)}
            className="w-full px-3 py-1.5 text-xs border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 bg-slate-50 focus:bg-white"
          />
          {searchFilter && (
            <button
              type="button"
              onClick={() => setSearchFilter('')}
              className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 text-xs font-bold cursor-pointer"
            >
              ✕
            </button>
          )}
        </div>
      </div>

      {/* Tables Detailed Grid */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-xs overflow-hidden">
        <div className="px-5 py-3.5 bg-slate-50 border-b border-slate-200 flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <Table className="w-4 h-4 text-blue-600" />
            <h4 className="text-xs font-bold text-slate-900">Veritabanı Tablo Listesi &amp; Kayıt Dökümü</h4>
          </div>
          <div className="text-[11px] text-slate-500 font-mono">
            Son Senkronizasyon: {lastRefreshedAt.toLocaleTimeString('tr-TR')}
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-100/70 text-[11px] font-bold text-slate-600 uppercase tracking-wider">
                <th className="px-4 py-2.5">Tablo Adı (MySQL / SQL)</th>
                <th className="px-4 py-2.5">Görünen Ad &amp; Kapsam</th>
                <th className="px-4 py-2.5 text-center">Sütun Sayısı</th>
                <th className="px-4 py-2.5 text-right">Toplam Kayıt</th>
                <th className="px-4 py-2.5">Durum / Açıklama</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 text-xs">
              {filteredTables.map((tbl, index) => {
                const isZero = tbl.count === 0;
                return (
                  <tr
                    key={tbl.name}
                    className={`hover:bg-blue-50/50 transition-colors ${
                      index % 2 === 0 ? 'bg-white' : 'bg-slate-50/40'
                    }`}
                  >
                    <td className="px-4 py-3 font-mono font-bold text-blue-700 whitespace-nowrap">
                      <div className="flex items-center space-x-2">
                        <span className="w-2 h-2 rounded-full bg-blue-500"></span>
                        <span>{tbl.name}</span>
                      </div>
                    </td>

                    <td className="px-4 py-3">
                      <div className="font-semibold text-slate-900">{tbl.displayName}</div>
                      <div className="text-[11px] text-slate-500 line-clamp-1">{tbl.description}</div>
                    </td>

                    <td className="px-4 py-3 text-center">
                      <span className="inline-flex items-center px-2 py-0.5 rounded-md bg-slate-100 text-slate-700 text-[11px] font-mono font-semibold">
                        {tbl.columnsCount} kolon
                      </span>
                    </td>

                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      <span
                        className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-black font-mono shadow-2xs ${
                          isZero
                            ? 'bg-slate-100 text-slate-500 border border-slate-200'
                            : 'bg-emerald-50 text-emerald-800 border border-emerald-300'
                        }`}
                      >
                        {tbl.count.toLocaleString('tr-TR')} kayıt
                      </span>
                    </td>

                    <td className="px-4 py-3 text-[11px] text-slate-600">
                      <div className="flex items-center space-x-1.5">
                        {isZero ? (
                          <span className="text-slate-400 italic">Henüz kayıt yok</span>
                        ) : (
                          <>
                            <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
                            <span className="font-medium text-slate-700">{tbl.details || 'Veriler hazır ve aktif'}</span>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {filteredTables.length === 0 && (
          <div className="p-8 text-center text-slate-400 text-xs">
            Aradığınız kritere uygun tablo bulunamadı.
          </div>
        )}
      </div>

      {/* SQL & Backup Tools Quick Box */}
      <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 space-y-3">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
          <div className="flex items-center space-x-2">
            <HardDrive className="w-4 h-4 text-blue-600" />
            <div>
              <h4 className="text-xs font-bold text-slate-900">Veritabanı Dışa Aktarma &amp; İçe Aktarma Araçları</h4>
              <p className="text-[11px] text-slate-500">
                Tüm tabloları MySQL uyumlu tam SQL yedeği veya taşınabilir JSON dosyası olarak alabilirsiniz.
              </p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 pt-1">
          {/* 1. SQL */}
          <div className="bg-white p-3 rounded-lg border border-slate-200 flex flex-col justify-between space-y-2.5">
            <div>
              <div className="flex items-center space-x-1.5 text-blue-600 font-bold text-xs">
                <Table className="w-3.5 h-3.5" />
                <span>MySQL .sql Dökümü</span>
              </div>
              <p className="text-[11px] text-slate-500 mt-1">
                phpMyAdmin / cPanel için tüm CREATE TABLE ve INSERT komutlarını içeren hazır dosya.
              </p>
            </div>
            <button
              type="button"
              onClick={handleDownloadSql}
              disabled={downloadingSql}
              className="w-full py-1.5 px-3 bg-blue-600 hover:bg-blue-500 disabled:bg-blue-400 text-white rounded-lg text-xs font-bold flex items-center justify-center space-x-1.5 cursor-pointer transition-colors"
            >
              <Download className={`w-3.5 h-3.5 ${downloadingSql ? 'animate-bounce' : ''}`} />
              <span>{downloadingSql ? 'Oluşturuluyor...' : 'MySQL SQL İndir'}</span>
            </button>
          </div>

          {/* 2. JSON */}
          <div className="bg-white p-3 rounded-lg border border-slate-200 flex flex-col justify-between space-y-2.5">
            <div>
              <div className="flex items-center space-x-1.5 text-indigo-600 font-bold text-xs">
                <FileCode className="w-3.5 h-3.5" />
                <span>Tam JSON Yedeği</span>
              </div>
              <p className="text-[11px] text-slate-500 mt-1">
                Tek tıkla başka cihaza aktarmak veya sistem durumunu saklamak için JSON formatı.
              </p>
            </div>
            <button
              type="button"
              onClick={downloadServerDataJson}
              className="w-full py-1.5 px-3 bg-indigo-600 hover:bg-indigo-500 text-white rounded-lg text-xs font-bold flex items-center justify-center space-x-1.5 cursor-pointer transition-colors"
            >
              <Download className="w-3.5 h-3.5" />
              <span>JSON Yedeği İndir</span>
            </button>
          </div>

          {/* 3. Restore */}
          <div className="bg-white p-3 rounded-lg border border-slate-200 flex flex-col justify-between space-y-2.5">
            <div>
              <div className="flex items-center space-x-1.5 text-amber-600 font-bold text-xs">
                <Upload className="w-3.5 h-3.5" />
                <span>Yedekten Geri Yükle</span>
              </div>
              <p className="text-[11px] text-slate-500 mt-1">
                Daha önce alınan JSON yedeğini yükleyerek tüm tabloları sıfırlamadan güncelleyin.
              </p>
            </div>
            <label
              className={`w-full py-1.5 px-3 bg-amber-600 hover:bg-amber-500 text-white rounded-lg text-xs font-bold flex items-center justify-center space-x-1.5 cursor-pointer transition-colors ${
                restoringBackup ? 'opacity-50 pointer-events-none' : ''
              }`}
            >
              <Upload className="w-3.5 h-3.5" />
              <span>{restoringBackup ? 'Yükleniyor...' : 'Yedek Dosyası Seç'}</span>
              <input
                type="file"
                accept=".json"
                onChange={handleRestoreFile}
                className="hidden"
                disabled={restoringBackup}
              />
            </label>
          </div>
        </div>
      </div>
    </div>
  );
};
